// Constants for various configurations
const VARIANT_ID = 48889375818032; // Unique ID for the gift wrapping variant
const PRICE_REGEX = /[\d|,|\.]+/; // Regex to match price formats
const NUMBER_REGEX = /\d+/; // Regex to match numbers
const WRAP_PRICE = 5; // Price of gift wrapping
const SESSION_KEY = "gift_wrap_session"; // Key to store gift wrap session data

// Options to format numbers to 2 decimal places
const NUMBER_LOCALE_OPTIONS = {
	minimumFractionDigits: 2,
	maximumFractionDigits: 2
};

// Function to prevent checkout while a task is running
async function preventCheckout(callback) {
	const disabledElements = Array.from(
		document.querySelectorAll(
			"#CartDrawer-Checkout, .select__select, .select__checkbox"
		)
	);

	// Disable checkout-related elements
	disabledElements.forEach(elm => {
		if (!elm) return;
		elm.disabled = true;
	});

	const result = await callback();

	// Re-enable the checkout-related elements
	disabledElements.forEach(elm => {
		if (!elm) return;
		elm.disabled = false;
	});

	return result;
}

// Custom element class for managing gift wrapping (re-renders on the cart update)
class GiftWrapping extends HTMLElement {
	constructor() {
		super();

		// Elements related to the total price and gift wrap checkbox
		this.price = this.querySelector(".totals__total-value");
		this.checkbox = this.querySelector("input#gift-wrap-checkbox");
		this.initialRender = true;
	}

	connectedCallback() {
		// Retrieve session data
		const { checked } = getParsedSessionProgress();

		// If wrapping was previously selected, update the hidden input and checkbox states
		if (checked) {
			const hiddenInput = document.querySelector("#is-wrapping");
			hiddenInput.checked = true;
			this.checkbox.checked = true;
		}

		// Set the cart state based on the checkbox status
		this.setCartState(checked);

		// Add event listener for checkbox changes
		this.checkbox.addEventListener("change", e => {
			e.stopPropagation();
			const isChecked = e.target.checked;

			this.setCartState(isChecked);

			// Store the session state after checkbox change
			storeSessionProgress(undefined, isChecked);
		});

		this.initialRender = false;
	}

	// Set the cart state based on whether gift wrapping is selected
	setCartState(checked = false) {
		preventCheckout(async () => {
			// If it's the initial render and wrapping is not selected, clear session
			if (this.initialRender && !checked) {
				clearSessionProgress();
				return;
			}

			// Restore or clear wrapping items based on checkbox status
			const data = await preventCheckout(async () => {
				if (checked) {
					return this.restoreWappingItems();
				} else {
					return this.clearWrappingItems();
				}
			});

			// Calculate item and wrapping quantities from cart data
			const [itemQuantity, wrappingQuantity] = (data?.items || []).reduce(
				(acc, item) => {
					if (item.variant_id !== VARIANT_ID) {
						acc[0] += item.quantity;
					} else {
						acc[1] += item.quantity;
					}
					return acc;
				},
				[0, 0]
			);

			// Update the cart price and item count display
			updateCartPrice(data);
			this.setItemCount(itemQuantity);
		});
	}

	// Restore wrapping items based on session data
	async restoreWappingItems() {
		const { values } = getParsedSessionProgress();
		if (!values) return;

		let initialData = await getCartData();

		// Filter and map session values to unique variant values in the cart
		const uniqueValues = Array.from(
			new Set(
				values
					.filter(v =>
						initialData.items.some(
							item => item.variant_id === parseInt(Object.keys(v)[0])
						)
					)
					.map(v => Object.values(v)[0])
			)
		);

		// Prepare form data for updating the cart with wrapping items
		const formData = {
			updates: {
				[VARIANT_ID]: uniqueValues.length
			}
		};

		// Update the cart with the wrapping items
		const data = await fetch(window.Shopify.routes.root + "cart/update.js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(formData)
		})
			.then(res => res.json())
			.catch(error => {
				console.error("Error:", error);
			});

		return data;
	}

	// Clear wrapping items from the cart
	async clearWrappingItems() {
		const formData = {
			updates: {
				[VARIANT_ID]: 0
			}
		};

		// Update the cart to remove wrapping items
		const data = await fetch(window.Shopify.routes.root + "cart/update.js", {
			method: "POST",
			headers: {
				"Content-Type": "application/json"
			},
			body: JSON.stringify(formData)
		})
			.then(res => res.json())
			.catch(error => {
				console.error("Error:", error);
			});

		return data;
	}

	// Set the item count display in the cart UI
	setItemCount(count) {
		const selector = ".drawer__heading__count, .cart-count-bubble__value";
		const elements = Array.from(document.querySelectorAll(selector));

		// Update all elements with the new item count
		elements.forEach(elm => {
			elm.textContent = elm.textContent.replace(NUMBER_REGEX, count);
		});
	}
}

// Define the 'gift-wrapping' custom element
customElements.define("gift-wrapping", GiftWrapping);

// Custom element class for managing individual gift wrapping selection
class WrapSelect extends HTMLElement {
	constructor() {
		super();

		this.initialRender = true; // Flag for initial render
		this.index = parseInt(this.dataset.index); // Index for tracking
		this.checkbox = this.querySelector(".select__checkbox"); // Checkbox element
		this.select = this.querySelector(".select__select"); // Select dropdown element
	}

	connectedCallback() {
		// Populate the select dropdown with option values
		this.addOptionValues();

		// Add event listeners for select and checkbox changes
		this.select.addEventListener("change", this.onSelectChange.bind(this));
		this.checkbox.addEventListener("change", this.onCheckboxChange.bind(this));

		this.initialRender = false;
	}

	// Add option values to the select dropdown based on session data or existing selections
	addOptionValues() {
		const { values: storedValues, checked } = getParsedSessionProgress();

		const count =
			this.initialRender && storedValues
				? storedValues.length
				: document.querySelectorAll("wrap-select .select__checkbox:checked")
						.length;

		// Remove all existing options
		this.select.innerHTML = "";

		for (let i = 1; i <= count; i++) {
			const value = `Gave ${i}`;
			const node = document.createElement("option");

			node.dataset.index = i;
			node.value = value;
			node.textContent = value;

			// If initial render and wrapping was previously selected, restore the selection
			if (this.initialRender & checked && storedValues) {
				const storedValue = storedValues.find(v => v[this.dataset.variant_id]);
				if (storedValue) {
					const [_, selectValue] = Object.entries(storedValue)[0];
					if (value === selectValue) {
						this.select.value = selectValue;
						node.selected = true;
					}
					this.checkbox.checked = true;
				}
			}

			// Append the option node to the select dropdown
			this.select.appendChild(node);
		}
	}

	// Update option values for other selects based on the current select's index
	setOptionValues() {
		const selects = document.querySelectorAll(
			"wrap-select:has(.select__checkbox:checked)"
		);

		selects.forEach((select, i) => {
			if (select.dataset.index === this.dataset.index) {
				this.select.value = `Gave ${i + 1}`;
			}
		});
	}

	// Handle checkbox state change
	onCheckboxChange(e) {
		e.stopPropagation();
		const checked = e.target.checked;

		const elements = document.querySelectorAll("wrap-select");

		// Update option values and select state
		elements.forEach(element => {
			element.addOptionValues();
			element.setOptionValues();
		});

		// Update the cart based on the checkbox state
		this.updateCart(checked);
	}

	// Handle select dropdown value change
	onSelectChange(e) {
		e.stopPropagation();

		const { count } = this.getActiveSelectValues();

		// Update the cart, checking if the number of selected items has decreased
		this.updateCart(this.last_count > count);
	}

	// Update the cart based on the increment (add/remove) state
	updateCart(increment = true) {
		const values = this.getActiveSelectValues().values;
		const isChecked = document.querySelector("#gift-wrap-checkbox").checked;

		// Store the session state with the selected values and checkbox status
		storeSessionProgress(values, isChecked);

		// Prevent checkout while the cart is being updated
		preventCheckout(async () => {
			if (increment) {
				await this.increase();
			} else {
				await this.decrease();
			}
		});
	}

	// Get active select values and count of unique selections
	getActiveSelectValues() {
		const checked = Array.from(
			document.querySelectorAll("wrap-select:has(.select__checkbox:checked)")
		);
		const values = checked.map(elm => ({
			[elm.dataset.variant_id]: elm.querySelector(".select__select").value
		}));

		const uniqueValues = Array.from(
			new Set(values.map(v => Object.values(v)[0]))
		);

		this.last_count = uniqueValues.length;

		return { values, count: this.last_count };
	}

	// Increase the quantity of wrapping items in the cart
	async increase() {
		try {
			const formData = {
				items: [
					{
						id: VARIANT_ID,
						quantity: 1
					}
				]
			};

			// Add wrapping item to the cart
			await fetch(window.Shopify.routes.root + "cart/add.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(formData)
			}).then(res => res.json());

			// Get updated cart data and refresh the price
			const data = await getCartData();
			updateCartPrice(data);
		} catch (err) {
			console.error(err);
			window.alert("An error occurred while adding gift wrapping");
		}
	}

	// Decrease the quantity of wrapping items in the cart
	async decrease() {
		try {
			const { items } = await getCartData();

			const wrappingItem = items.find(item => item.variant_id === VARIANT_ID);

			if (wrappingItem) {
				const formData = {
					updates: {
						[VARIANT_ID]: wrappingItem.quantity - 1
					}
				};

				// Update the cart to remove one wrapping item
				const data = await fetch(
					window.Shopify.routes.root + "cart/update.js",
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json"
						},
						body: JSON.stringify(formData)
					}
				).then(res => res.json());

				// Refresh the cart price after removal
				updateCartPrice(data);
			}
		} catch (err) {
			console.error(err);
			window.alert("An error occurred while removing gift wrapping");
		}
	}
}

// Function to retrieve the current cart data
async function getCartData() {
	return fetch(window.Shopify.routes.root + "cart.js", {
		headers: {
			"Content-Type": "application/json"
		}
	}).then(res => res.json());
}

// Function to update the cart's price display
async function updateCartPrice(data) {
	// Get cart data if not provided
	if (!data) {
		data = await getCartData();
	}

	console.log("data", data);
	const { values } = getParsedSessionProgress();
	console.log("values", values);

	// Calculate total price and format it for display
	const total = data.total_price / 100;
	const totalElement = document.querySelector(".totals__total-value");

	const targetPrice = total.toLocaleString(
		window.Shopify.locale,
		NUMBER_LOCALE_OPTIONS
	);

	// Update total price display
	totalElement.textContent = totalElement.textContent.replace(
		PRICE_REGEX,
		targetPrice
	);

	// Get the quantity of wrapping items in the cart
	const count =
		data.items.find(item => item.variant_id === VARIANT_ID)?.quantity ?? 0;

	// Calculate and format the total price for wrapping items
	const countPrice = WRAP_PRICE * Math.max(count, 1);
	const countPriceElement = document.querySelector("#cart__gift-wrap__total");

	const targetCountPrice = countPrice.toLocaleString(
		window.Shopify.locale,
		NUMBER_LOCALE_OPTIONS
	);

	// Update the wrapping items price display
	countPriceElement.textContent = countPriceElement.textContent.replace(
		PRICE_REGEX,
		targetCountPrice
	);

	// Update the cart note with the selected gift wrapping options
	const cartNote = values
		?.map(v => {
			const key = Object.keys(v)[0];
			const value = Object.values(v)[0];

			return `${key} - ${value}`;
		})
		.join(", ");

	// Create an array of line item objects to update properties
	let cartLineItems = values?.map(v => {
		const key = Object.keys(v)[0];
		const value = Object.values(v)[0];
		return {
			id: key,
			properties: {
				Gaveindpakning: value
			}
		};
	});
	// set every product that is not in value to have a empty properties
	data.items.forEach(item => {
		if (!values.some(v => Object.keys(v)[0] == item.variant_id)) {
			cartLineItems.push({
				id: "" + item.variant_id,
				properties: { Gaveindpakning: "" }
			});
		}
	});

	// Loop through each item and update it individually
	for (const item of cartLineItems) {
		const formData = {
			id: item.id,

			properties: item.properties
		};

		try {
			const response = await fetch("/cart/change.js", {
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(formData)
			});
			const result = await response.json();
			console.log("Item updated:", result);
		} catch (error) {
			console.error("Error updating item:", error);
		}
	}

	// Optionally, update cart note after all items are processed
	const formDataNote = {
		note: cartNote
	};

	try {
		const noteResponse = await fetch(
			window.Shopify.routes.root + "cart/update.js",
			{
				method: "POST",
				headers: {
					"Content-Type": "application/json"
				},
				body: JSON.stringify(formDataNote)
			}
		);
		const noteResult = await noteResponse.json();
		console.log("Cart note updated:", noteResult);
	} catch (error) {
		console.error("Error updating cart note:", error);
	}
}

// Function to clear session progress data
function clearSessionProgress() {
	sessionStorage.removeItem(SESSION_KEY);
}

// Function to store session progress data (gift wrap selections)
function storeSessionProgress(values, checked) {
	const data = getParsedSessionProgress();
	if (values !== undefined) {
		data.values = values;
	}
	if (checked !== undefined) {
		data.checked = checked;
	}
	sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
}

// Function to retrieve and parse session progress data
function getParsedSessionProgress() {
	try {
		const stored = sessionStorage.getItem(SESSION_KEY);
		if (!stored) return {};
		return JSON.parse(stored);
	} catch (_) {
		return {};
	}
}

// Define the 'wrap-select' custom element
customElements.define("wrap-select", WrapSelect);
